import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { cities, hotels } from "../data/data";
import { Star, MapPin } from "lucide-react";

// City images mapping
const cityImages = {
  antalya: "/images/hotels/antalya/antalya_old_town.jpg",
  belek: "/images/hotels/antalya/antalya_belek.jpg",
  side: "/images/hotels/antalya/antalya_side.jpg",
  kizilagac: "/images/hotels/antalya/antalya_kizilagac.jpg",
  alanya: "/images/hotels/antalya/antalya_alanya.jpg",
  kemer: "/images/hotels/antalya/antalya_kemer.jpg",
  kas: "/images/hotels/antalya/antalya_kas.jpg",
  kumluca: "/images/hotels/antalya/antalya_kumluca.jpg",
};

const Hotels = () => {
  const { language, t } = useLanguage();
  const [selectedCity, setSelectedCity] = useState(null);

  const filteredHotels = selectedCity
    ? hotels.filter((hotel) => hotel.city === selectedCity)
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-yellow-50">
      {/* Header with Background Image */}
      <section className="relative bg-gradient-to-r from-slate-900 to-slate-800 text-white py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920&q=80"
            alt="Hotels Background"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-slate-800/80" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-5xl font-bold mb-4 text-center">{t("hotels")}</h1>
          <p className="text-xl text-gray-300 text-center max-w-2xl mx-auto">
            {t("selectCity")}
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        {/* City Selection */}
        {!selectedCity && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {cities.map((city) => (
              <button
                key={city.id}
                onClick={() => setSelectedCity(city.id)}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                {/* City Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={cityImages[city.id]}
                    alt={city.name[language]}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white">
                      {city.name[language]}
                    </h3>
                  </div>
                </div>

                {/* Hotel Count */}
                <div className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                    <MapPin className="h-8 w-8 text-white" />
                  </div>
                  <p className="text-amber-600 font-semibold text-lg">
                    {hotels.filter((h) => h.city === city.id).length}{" "}
                    {t("hotels")}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Hotel List */}
        {selectedCity && (
          <div>
            <div className="mb-8 flex items-center justify-between">
              <h2 className="text-3xl font-bold text-gray-900">
                {cities.find((c) => c.id === selectedCity)?.name[language]}{" "}
                {t("hotelsIn")}
              </h2>
              <button
                onClick={() => setSelectedCity(null)}
                className="text-amber-600 hover:text-amber-700 font-semibold transition-colors"
              >
                ← {t("selectCity")}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredHotels.map((hotel) => (
                <div
                  key={hotel.id}
                  className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={hotel.image}
                      alt={hotel.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center space-x-1">
                      <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                      <span className="font-semibold text-gray-900">
                        {hotel.stars}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {hotel.name}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {hotel.description[language]}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Hotels;
